import { userAvatarBucketName } from '@/config/env';
import { GradientAvatar } from '@/shared/components';
import {
  Box,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { User } from '../../models/user';

export interface UserItemProps {
  user: User;
  isSelected?: boolean;
  showPosition?: boolean;
  onClick?: (user: User) => void;
  disabled?: boolean;
  className?: string;
  variant?: 'list' | 'autocomplete';
}

/**
 * UserItemContent - Component nội dung chung cho cả List và Autocomplete
 */
export const UserItemContent = memo<{
  user: User;
  showPosition?: boolean;
  avatarSize?: number;
}>(function UserItemContent({ user, showPosition = true, avatarSize = 40 }) {
  const { t } = useTranslation();

  return (
    <>
      <GradientAvatar
        src={user.profile.avatarRef || undefined}
        bucketName={userAvatarBucketName}
        peerId={user.id}
        name={user.fullName}
        sx={{
          width: avatarSize,
          height: avatarSize,
          fontSize: avatarSize * 0.4,
          flexShrink: 0
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant='body2'
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            lineHeight: 1.5
          }}
          noWrap
        >
          {user.fullName}
        </Typography>
        <Typography
          variant='caption'
          sx={{
            color: 'text.secondary',
            display: 'block',
            lineHeight: 1.5
          }}
          noWrap
        >
          {user.email || t('participant.userItem.noEmail')}
        </Typography>
        {showPosition && (
          <Typography
            variant='caption'
            sx={{
              color: 'text.secondary',
              display: 'block',
              mt: 0.5
            }}
            noWrap
          >
            {[user.chucVu, user.donVi].filter(Boolean).join(' - ')}
          </Typography>
        )}
      </Box>
    </>
  );
});

/**
 * UserItem - Component hiển thị thông tin người dùng
 * Hỗ trợ 2 variants: 'list' (default) và 'autocomplete'
 */
export const UserItem = memo<UserItemProps>(function UserItem({
  user,
  isSelected = false,
  onClick,
  className,
  disabled = false,
  showPosition = true,
  variant = 'list'
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick(user);
    }
  };

  // Variant for Autocomplete dropdown
  if (variant === 'autocomplete') {
    return (
      <Box
        onClick={handleClick}
        className={className}
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          px: 2,
          py: 1,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          borderRadius: 1,
          transition: theme.transitions.create(['background-color'], {
            duration: theme.transitions.duration.shortest
          }),
          '&:hover': {
            backgroundColor: !disabled ? 'action.hover' : 'transparent'
          },
          ...(isSelected && {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12)
            }
          })
        }}
      >
        <UserItemContent user={user} showPosition={showPosition} avatarSize={40} />
      </Box>
    );
  }

  // Default variant for List
  return (
    <ListItem
      key={user.id}
      disablePadding
      className={className}
      sx={{
        mb: 0.5
      }}
    >
      <ListItemButton
        onClick={handleClick}
        disabled={disabled}
        selected={isSelected}
        sx={{
          borderRadius: 1,
          px: 1.5,
          py: 1,
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12)
            }
          }
        }}
      >
        <ListItemAvatar>
          <GradientAvatar
            src={user.profile.avatarRef || undefined}
            bucketName={userAvatarBucketName}
            peerId={user.id}
            name={user.fullName}
            sx={{
              width: 40,
              height: 40,
              fontSize: 16
            }}
          />
        </ListItemAvatar>

        <ListItemText
          primary={
            <Typography
              variant='body2'
              sx={{
                fontWeight: 500,
                color: 'text.primary'
              }}
              noWrap
            >
              {user.fullName}
            </Typography>
          }
          secondary={
            <Box component='span' sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography variant='caption' sx={{ color: 'text.secondary' }} noWrap>
                {user.email || t('participant.userItem.noEmail')}
              </Typography>
              {showPosition && (
                <Typography variant='caption' sx={{ color: 'text.secondary' }} noWrap>
                  {[user.chucVu, user.donVi].filter(Boolean).join(' - ')}
                </Typography>
              )}
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>
  );
});
